<script setup lang="ts">
import {
  RouterView,
  useRoute,
  useRouter,
  type NavigationGuardNext,
  type RouteLocationNormalized
} from 'vue-router'
import { onMounted, watch, ref, onBeforeUnmount } from 'vue'
import { i18nService } from '@accor/customer-vue-services'
import PageLayout from '@/components/organisms/layout/PageLayout.vue'
import { useWebAppStore } from '@/stores/webApp'
import GlobalSkeleton from '@/components/GlobalSkeleton.vue'
import { useUserStore } from '@/stores/user'
import { AdsSnackBarContainer } from '@accor/ads-components'
import { useEmployeeStore } from '@/stores/employee'
import { useCompanyStore } from '@/stores/company'
import TermsAndConditionsAcceptanceModal from '@/components/organisms/compliance/TermsAndConditionsAcceptanceModal.vue'

const isVue2Page = ref(false)
const router = useRouter()
const route = useRoute()
const webAppStore = useWebAppStore()
const isLoading = ref(false)
const userStore = useUserStore()
const employeeStore = useEmployeeStore()
const companyStore = useCompanyStore()
let appVue2

const beforeEachHandler = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const webAppStore = useWebAppStore()
  const isTabletMode = webAppStore.isTabletMode
  isLoading.value = true

  if (to.name === 'locale') {
    if (!i18nService.isSupportedLocale(to.params.locale)) {
      next()
    } else if (isTabletMode) {
      /*
       * Do not change this way of redirecting
       * using next({ name: 'mobile-nav' }) will not work and redirect to "/account"
       * and next(router.resolve({ name: 'mobile-nav' }).href) will redirect to "/account/account/mobile-nav" in SPA mode
       */
      next(router.resolve({ name: 'mobile-nav' }))
    } else {
      next(router.resolve({ name: 'dashboard' }))
    }
  } else {
    if (to.name !== from.name) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    next()
  }
}

const finishLoading = () => (isLoading.value = false)
const syncVue3PathFromVue2 = () => {
  const path = appVue2?.$router.currentRoute.path
  if (path) router.push(path)
}

const vue2ReadyPromise = new Promise((resolve) => {
  const listener = () => {
    document.removeEventListener('vue2-ready', listener)
    appVue2 = window.appVue2
    appVue2?.$router.beforeEach(beforeEachHandler)
    appVue2?.$router.afterEach(() => {
      finishLoading()
      syncVue3PathFromVue2()
    })
    appVue2?.$router.onError(finishLoading)

    resolve(true)
  }
  document.addEventListener('vue2-ready', listener)
})

router.beforeEach(beforeEachHandler)
router.afterEach(finishLoading)
router.onError(finishLoading)

const hasGuestNotifications = () => {
  const guestNotifications = appVue2?.$store?.getters?.guestNotifications
  return !!guestNotifications && Object.keys(guestNotifications).length > 0
}

const syncGuestNotifications = () => {
  appVue2?.$store?.dispatch?.('initOrders')
  userStore.setGuestNotifications(hasGuestNotifications())
  appVue2?.$store?.subscribe?.(() => {
    userStore.setGuestNotifications(hasGuestNotifications())
  })
}

watch(
  () => route,
  (newValue) => {
    const {
      meta: { isVue2, displaySideNav = true },
      path
    } = newValue
    isVue2Page.value = Boolean(isVue2)
    if (isVue2 && appVue2?.$router.currentRoute.path !== path) {
      appVue2?.$router.push({ path })
      webAppStore.setNavigationDisplay(displaySideNav as boolean)
    }
  },
  { immediate: true, deep: true }
)

onMounted(async () => {
  await i18nService.readyPromise
  await userStore.refreshUser()

  if (userStore.isAab) {
    if (!employeeStore.employee) {
      await employeeStore.setEmployee()
    }

    await companyStore.setCompany()
  }

  // Send event that the user store is ready to vue2
  window.vue3UserReady = true
  document.dispatchEvent(new Event('vue3-UserReady'))
  await vue2ReadyPromise
  syncGuestNotifications()

  // Create a Vue 2 instance
  appVue2.$mount('#app-account2')
  //Restore __VUE_HMR_RUNTIME__ from Vue 3. To be removed when Vue2 is gone
  window.__VUE_HMR_RUNTIME__ = window.__VUE_HMR_RUNTIME__BACKUP
  window.addEventListener('resize', webAppStore.setWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', webAppStore.setWidth)
})
</script>

<template>
  <div id="account-container">
    <PageLayout>
      <metainfo>
        <template #title="{ content }">{{ content }}</template>
      </metainfo>
      <div v-show="isVue2Page">
        <div id="app-account2"></div>
      </div>
      <GlobalSkeleton v-show="isLoading" />
      <RouterView />
      <AdsSnackBarContainer />
      <TermsAndConditionsAcceptanceModal />
    </PageLayout>
  </div>
</template>
